import React, { Children, FC, PropsWithChildren, ReactChild, useMemo, useRef, createContext, useContext } from 'react'

import Icon from 'core/components/Icon'
import Tooltip from 'core/components/lib/Tooltip'
import { R } from 'core/helpers'
import variables from 'core/styles/variables'

import {
  StyledTable,
  StyledTableButtonWrapper,
  StyledTableCell,
  StyledTableHeaderCell,
  StyledTableHeaderContent,
  StyledTableHeaderRow,
  StyledTableRow,
  StyledTableValue,
  StyledTableWrapper,
} from './styles'
import { TableLoadingOverlay, TableTextOverlay } from './TableOverlay'
import { useStickyCellMeasurements } from './useStickyCellMeasurements'

// Define Context
/**
 * @typedef TableContextProps
 * @description Defines the shape of the context object provided by `TableContext`.
 * This context shares internal table state with descendant components.
 * @property {Array<ProcessedColumnDefinition<any>>} [processedColumns] - Array of fully processed column definitions, including actual widths and pinning state.
 * @property {Array<number>} [pinnedColumnIndicesFromCtx] - Array of indices for columns that are currently pinned, derived from props.
 * @property {any} [measurementTriggerFromCtx] - A value that changes to trigger re-measurement of sticky cells, derived from props.
 */
type TableContextProps = {
  processedColumns?: Array<ProcessedColumnDefinition<any>>
  pinnedColumnIndicesFromCtx?: Array<number>
  measurementTriggerFromCtx?: any
}

const TableContext = createContext<TableContextProps | null>(null)

// Custom hook to use the context, ensuring it's not null
const useTableContext = () => {
  const context = useContext(TableContext)
  if (!context) {
    throw new Error('useTableContext must be used within a TableContext.Provider')
  }
  return context
}

/**
 * @typedef ColumnDefinition
 * @description Defines the public API for a column configuration.
 * Users of the `Table` component will provide an array of these to define columns.
 * @template T - A string literal type representing the possible keys for sortable columns.
 * @property {ReactChild} label - The content to render in the column header.
 * @property {string} [tooltip] - Optional tooltip text to display next to the column header label.
 * @property {T} [sortKey] - Optional key used for sorting this column. If provided, the column header becomes clickable for sorting.
 * @property {boolean} [pinned] - Optional flag to indicate if the column should be pinned (sticky). Overridden by `pinnedColumnIndices` prop on Table if provided.
 * @property {string | number} [width] - Optional width for the column (e.g., '150px', 150). Defaults to `defaultColumnWidth` on the Table.
 * @property {'left' | 'center' | 'right'} [align] - Optional text alignment for the cells in this column. Defaults to 'left'.
 * @property {string} [key] - Optional unique key for the column, useful if `label` is not a simple string or for React's key prop.
 */
export type ColumnDefinition<T extends string> = {
  label: ReactChild
  tooltip?: string
  sortKey?: T
  pinned?: boolean
  width?: string | number
  align?: 'left' | 'center' | 'right'
  key?: string
}

// Extend ColumnDefinition with internal properties used by the Table component
/**
 * @typedef ProcessedColumnDefinition
 * @description Internal representation of a column, extending `ColumnDefinition` with calculated values used by the `Table` for rendering.
 * @template T - A string literal type representing the possible keys for sortable columns.
 * @property {string} _actualWidth - The calculated actual width of the column as a string (e.g., '150px').
 * @property {boolean} _isPinned - Boolean indicating if the column is currently pinned.
 * @property {boolean} _isLastPinned - Boolean indicating if this is the last pinned column in the sequence.
 * @property {number} [_left] - The calculated left offset for sticky positioning, if pinned.
 */
type ProcessedColumnDefinition<T extends string> = ColumnDefinition<T> & {
  _actualWidth: string
  _isPinned: boolean
  _isLastPinned: boolean
  _left?: number
}

/**
 * @typedef TableProps
 * @description Defines the props accepted by the main `Table` component.
 * @template T - A string literal type representing the possible keys for sortable columns, passed down from `ColumnDefinition`.
 * @property {boolean} [bordered] - If true, applies a border to the table and its cells. Defaults to false.
 * @property {Array<ColumnDefinition<T>>} [columns] - Array of column definitions.
 * @property {Array<number>} [pinnedColumnIndices] - Explicitly sets which columns (by index) are pinned. Overrides `pinned` in individual `ColumnDefinition`s.
 * @property {string} [entitiesLabel] - Label for the items in the table, used in empty/error messages (e.g., "No users found"). Defaults to 'results'.
 * @property {string} [errorMessage] - Custom error message to display when `isError` is true.
 * @property {boolean} [hideHeader] - If true, the table header will not be rendered. Defaults to false.
 * @property {boolean} [isError] - If true, displays an error message overlay. Defaults to false.
 * @property {boolean} [isLoading] - If true, displays a loading overlay. Defaults to false.
 * @property {(key: T) => void} [onSort] - Callback function triggered when a sortable column header is clicked. Receives the `sortKey`.
 * @property {T} [sortBy] - The `sortKey` currently used for sorting. Prepending '-' indicates descending order.
 * @property {string | number} [defaultColumnWidth] - Default width for columns if not specified in `ColumnDefinition`. Defaults to '150px'.
 * @property {any} [measurementTrigger] - A prop that, when changed, can trigger re-measurement of sticky column dimensions.
 */
type TableProps<T extends string> = PropsWithChildren<{
  bordered?: boolean
  columns?: Array<ColumnDefinition<T>>
  pinnedColumnIndices?: Array<number>
  entitiesLabel?: string
  errorMessage?: string
  hideHeader?: boolean
  isError?: boolean
  isLoading?: boolean
  onSort?: (key: T) => void
  sortBy?: T
  defaultColumnWidth?: string | number
  measurementTrigger?: any
}>

/**
 * @component Table
 * @description
 * A flexible and themable Table component with support for sorting, column pinning,
 * customizable column widths, and optional borders. It uses React Context
 * to manage and provide table state (like processed columns and pinning information)
 * to its child components (e.g., `Table.Row`, `Table.Cell`).
 *
 * @template T - A string literal type representing the possible keys for sortable columns.
 *
 * @example
 * const columns = [
 *   { label: 'Name', sortKey: 'name', pinned: true, width: 200 },
 *   { label: 'Date', sortKey: 'date', width: 150 },
 *   { label: 'Amount', align: 'right' },
 * ];
 *
 * <Table<typeof columns[number]['sortKey']>
 *   columns={columns}
 *   sortBy={currentSortKey}
 *   onSort={handleSort}
 *   pinnedColumnIndices={[0]}
 * >
 *   {data.map(item => (
 *     <Table.Row key={item.id}>
 *       <TableCell>{item.name}</TableCell>
 *       <TableCell>{formatDate(item.date)}</TableCell>
 *       <TableCell>{formatCurrency(item.amount)}</TableCell>
 *     </Table.Row>
 *   ))}
 * </Table>
 */
const Table = <T extends string>({
  bordered = false,
  children,
  columns = [],
  entitiesLabel = 'results',
  errorMessage,
  hideHeader = false,
  isError = false,
  isLoading = false,
  onSort,
  sortBy,
  defaultColumnWidth = '150px',
  pinnedColumnIndices = [],
  measurementTrigger,
}: TableProps<T>) => {
  const isEmpty = Children.count(children) === 0
  const tableElementRef = useRef<HTMLTableElement>(null)

  const processedColumns = useMemo((): Array<ProcessedColumnDefinition<T>> => {
    const defaultWidthStr = typeof defaultColumnWidth === 'number' ? `${defaultColumnWidth}px` : defaultColumnWidth

    const lastPinnedIndexInProp =
      pinnedColumnIndices && pinnedColumnIndices.length > 0 ? Math.max(...pinnedColumnIndices) : -1

    return R.map.indexed(columns ?? [], (col, index) => {
      const actualWidth =
        col.width ?
          typeof col.width === 'number' ?
            `${col.width}px`
          : col.width
        : defaultWidthStr

      const isPinnedByProp = pinnedColumnIndices ? pinnedColumnIndices.includes(index) : !!col.pinned

      const result: ProcessedColumnDefinition<T> = {
        ...col,
        _actualWidth: actualWidth,
        _isPinned: isPinnedByProp,
        _isLastPinned: isPinnedByProp && index === lastPinnedIndexInProp,
        _left: undefined,
      }

      return result
    })
  }, [columns, defaultColumnWidth, pinnedColumnIndices])

  const { itemRefs: headerCellRefs, calculatedOffsets: headerCellCalculatedOffsets } =
    useStickyCellMeasurements<HTMLTableCellElement>({
      itemIndicesToMeasure: pinnedColumnIndices || [],
      numberOfItems: processedColumns.length,
      measurementTrigger,
    })

  // This `contextValue` object is passed to the `TableContext.Provider` below.
  // Descendant components, such as `Table.Row`, can then access these values
  // (e.g., `processedColumns`, `pinnedColumnIndicesFromCtx`, `measurementTriggerFromCtx`)
  // by using the `useTableContext()` hook.
  const contextValue = useMemo(
    () => ({
      processedColumns: processedColumns,
      pinnedColumnIndicesFromCtx: pinnedColumnIndices,
      measurementTriggerFromCtx: measurementTrigger,
    }),
    [processedColumns, pinnedColumnIndices, measurementTrigger],
  )

  return (
    <TableContext.Provider value={contextValue}>
      <StyledTableWrapper $bordered={bordered}>
        <StyledTable ref={tableElementRef}>
          {!hideHeader && (
            <thead>
              <StyledTableHeaderRow>
                {R.map.indexed(processedColumns, (col, index) => {
                  const sorted = col.sortKey ? col.sortKey === sortBy || '-' + col.sortKey === sortBy : false

                  return (
                    <StyledTableHeaderCell
                      $isLastPinned={col._isLastPinned}
                      $left={col._isPinned ? headerCellCalculatedOffsets[index] : undefined}
                      $pinned={col._isPinned}
                      $sorted={sorted}
                      $width={col._actualWidth}
                      key={col.key ?? `header-cell-${index}`}
                      onClick={
                        col.sortKey ?
                          () => onSort?.(col.sortKey === sortBy ? (`-${col.sortKey}` as T) : col.sortKey!)
                        : undefined
                      }
                      ref={(el) => {
                        if (headerCellRefs.current) headerCellRefs.current[index] = el
                      }}
                    >
                      <StyledTableHeaderContent>
                        {col.label}
                        {col.sortKey && (
                          <Icon
                            color={sorted ? variables.colorBlack90 : variables.colorBlack40}
                            fontSize='24px'
                            margin='0 8px 0 0'
                            name={sorted && sortBy?.startsWith('-') ? 'arrow_drop_down' : 'arrow_drop_up'}
                          />
                        )}
                        {col.tooltip && <Tooltip content={col.tooltip} iconMargin='0 0 0 8px' />}
                      </StyledTableHeaderContent>
                    </StyledTableHeaderCell>
                  )
                })}
              </StyledTableHeaderRow>
            </thead>
          )}
          <tbody>
            {isEmpty ?
              <StyledTableRow $bordered>
                <StyledTableCell colSpan={processedColumns.length || 1}>&nbsp;</StyledTableCell>
              </StyledTableRow>
            : children}
          </tbody>
        </StyledTable>

        {isLoading ?
          <TableLoadingOverlay />
        : isError ?
          <TableTextOverlay>{errorMessage ?? `Error loading ${entitiesLabel}`}</TableTextOverlay>
        : isEmpty ?
          <TableTextOverlay>No {entitiesLabel} found</TableTextOverlay>
        : null}
      </StyledTableWrapper>
    </TableContext.Provider>
  )
}

/**
 * @typedef RowProps
 * @description Defines the props accepted by the `Table.Row` component.
 * @property {string} [className] - Optional CSS class to apply to the row.
 * @property {boolean} [bordered] - If true, the row will have a bottom border. Defaults to true (controlled by Table's bordered prop usually).
 * @property {boolean} [summary] - If true, applies styling for a summary row (e.g., increased padding). Defaults to false.
 * @property {boolean} [highlight] - If true, applies highlight styling on hover. Defaults to true if `onClick` is provided.
 * @property {boolean} [selected] - If true, applies styling to indicate the row is selected. Defaults to false.
 * @property {React.MouseEventHandler<HTMLTableRowElement>} [onClick] - Optional click handler for the row.
 */
type RowProps = PropsWithChildren<{
  className?: string
  bordered?: boolean
  summary?: boolean
  highlight?: boolean
  selected?: boolean
  onClick?: React.MouseEventHandler<HTMLTableRowElement>
}>

/**
 * @component Table.Row
 * @description
 * A sub-component of `Table` responsible for rendering a single row of data.
 * It consumes `TableContext` to access processed column information and pinning state,
 * which it uses to correctly render cells, including applying sticky positioning for pinned cells.
 * Cells within the row are typically direct children (e.g., `<TableCell>`).
 *
 * @example
 * <Table.Row key={item.id} onClick={() => handleRowClick(item)} selected={isSelected(item.id)}>
 *   <TableCell>{item.name}</TableCell>
 *   <TableCell>{item.date}</TableCell>
 * </Table.Row>
 */
const Row: FC<PropsWithChildren<RowProps>> = ({
  children,
  className,
  bordered = true,
  selected = false,
  summary = false,
  highlight,
  onClick,
}) => {
  // Consuming the context provided by TableContext.Provider in the main Table component.
  // This gives access to shared table state like processed columns and pinning information.
  const {
    processedColumns: contextProcessedColumns = [],
    pinnedColumnIndicesFromCtx = [],
    measurementTriggerFromCtx,
  } = useTableContext()

  const numCellsInRow = Children.count(children)

  const { itemRefs: dataCellRefs, calculatedOffsets: rowCellLeftOffsets } =
    useStickyCellMeasurements<HTMLTableCellElement>({
      itemIndicesToMeasure: pinnedColumnIndicesFromCtx,
      numberOfItems: numCellsInRow,
      measurementTrigger: measurementTriggerFromCtx,
    })

  return (
    <StyledTableRow
      $bordered={bordered}
      $highlight={highlight ?? !!onClick}
      $selected={selected}
      $summary={summary}
      className={className}
      onClick={onClick}
    >
      {Children.map(children, (childContent, index) => {
        const columnDef = contextProcessedColumns[index]
        const isCellPinned = columnDef ? columnDef._isPinned : pinnedColumnIndicesFromCtx.includes(index)
        const leftPositionForCell = isCellPinned ? rowCellLeftOffsets[index] : undefined

        return (
          childContent !== null && (
            <StyledTableCell
              $isLastPinned={columnDef?._isLastPinned && isCellPinned}
              $left={leftPositionForCell}
              $paddingBottom={summary ? '12px' : undefined}
              $paddingTop={summary ? '12px' : undefined}
              $pinned={isCellPinned}
              $textAlign={columnDef?.align}
              $width={columnDef?._actualWidth}
              key={`cell-${index}`}
              ref={(el) => {
                if (dataCellRefs.current) dataCellRefs.current[index] = el
              }}
            >
              {childContent}
            </StyledTableCell>
          )
        )
      })}
    </StyledTableRow>
  )
}

Table.Row = Row

const ValueWithEmphasis: FC<
  PropsWithChildren<{
    textAlign?: 'left' | 'center' | 'right'
  }>
> = ({ children, textAlign = 'left' }) => (
  <StyledTableValue $deemphasized={!children || Number(children) === 0} $textAlign={textAlign}>
    {children ?? '—'}
  </StyledTableValue>
)

Table.ValueWithEmphasis = ValueWithEmphasis

Table.CellActions = StyledTableButtonWrapper

export default Table
